import React, {
  useCallback, useEffect, useRef, useState
} from "react"
import { useSelector } from "react-redux"
import { Container, Pagination, Spinner } from "react-bootstrap"
import {
  NumberParam, StringParam, useQueryParam, withDefault
} from "use-query-params"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import SearchInput from "components/SearchInput"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import AnchorButton from "components/AnchorButton"
import { archiveAdminProduct, getAdminProducts } from "requesters/productRequester"
import { roleCheck } from "utils/index"
import { ADMIN } from "utils/constants"
import { getCurrentRole, getCurrentUser, getBaseUrl } from "apps/App/reducers"
import _ from "lodash"
import { apiErrorMessage, workflowLabel } from "./productSettings"
import ProductReviewIcon from "./ProductReviewIcon"

const toastOptions = {
  toastId: "products-admin",
  containerId: "main"
}

function productRangeLabel(listMeta) {
  if (listMeta.show_all) return `Showing all ${listMeta.total_count} products`

  const start = ((listMeta.current_page - 1) * listMeta.per_page) + 1
  const end = Math.min(listMeta.current_page * listMeta.per_page, listMeta.total_count)
  return `Showing ${start}–${end} of ${listMeta.total_count} products`
}

export default function ProductsIndex() {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser.attributes.role
  const [bySearch, setBySearch] = useQueryParam("by_search", StringParam)
  const [byStatus, setByStatus] = useQueryParam("by_status", withDefault(StringParam, "active"))
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 1))
  const [perPage, setPerPage] = useQueryParam("per_page", withDefault(StringParam, "25"))
  const [debouncedSearch, setDebouncedSearch] = useState(bySearch || "")
  const [products, setProducts] = useState([])
  const [requesting, setRequesting] = useState(false)
  const [listMeta, setListMeta] = useState(null)
  const filterMountRef = useRef(true)

  if (!roleCheck(systemRole, currentRole, ADMIN)) window.location = "/"

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(bySearch || ""), 350)
    return () => window.clearTimeout(id)
  }, [bySearch])

  useEffect(() => {
    if (filterMountRef.current) {
      filterMountRef.current = false
      return
    }
    setPage(1)
  }, [byStatus, debouncedSearch, setPage])

  const loadProducts = useCallback(async () => {
    setRequesting(true)
    const { response, error, meta } = await getAdminProducts(baseUrl, {
      by_search: debouncedSearch || undefined,
      by_status: byStatus,
      page,
      per_page: perPage
    })
    if (response) {
      setProducts(_.values(response.product || {}))
      const pagination = meta?.pagination
      if (pagination) {
        setListMeta(pagination)
        if (Number(page) !== Number(pagination.current_page)) setPage(pagination.current_page)
      }
    }
    if (error) {
      console.error(error)
      toast.error(apiErrorMessage(error, "Failed to load products"), toastOptions)
    }
    setRequesting(false)
  }, [baseUrl, byStatus, debouncedSearch, page, perPage, setPage])

  useEffect(() => {
    if (baseUrl) loadProducts()
  }, [baseUrl, loadProducts])

  const triggerArchive = useCallback(async (productId, archiveState) => {
    const { response, error } = await archiveAdminProduct(baseUrl, productId, archiveState)
    if (response) {
      toast.success(archiveState ? "Product archived" : "Product restored", toastOptions)
      loadProducts()
    }
    if (error) {
      console.error(error)
      toast.error(apiErrorMessage(error, "Failed to update product"), toastOptions)
    }
  }, [baseUrl, loadProducts])

  const clearFilters = () => {
    setBySearch(undefined)
    setByStatus("all")
    setPage(1)
    setPerPage("25")
  }

  const statusLabel = { active: "Active", archived: "Archived", all: "All" }[byStatus]

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center header-tabs-container">
        <div className="header-tabs">
          <h1>Products</h1>
        </div>
        <Link className="btn btn-outlined-blue" to="/settings/products/new" type="button">
          + New Product
        </Link>
      </div>
      <div className="table-listing-filters">
        <div className="btn-group mr-2">
          <SearchInput value={bySearch} setValue={setBySearch} placeholder="Search by name or code" />
        </div>
        <div className="btn-group mr-2">
          <FilterDropdown
            title="Status"
            filteredName={statusLabel}
            setFilter={setByStatus}
            hiddenAllOption
            options={[["Active", "active"], ["Archived", "archived"], ["All", "all"]]} />
        </div>
        <div className="btn-group">
          <AnchorButton className="f-small" onClick={clearFilters}>
            Clear Filters
          </AnchorButton>
        </div>
      </div>
      {!requesting && listMeta && (
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 mb-2 px-1">
          {listMeta.total_count > 0 && (
            <div className="text-muted small mb-2 mb-md-0">
              {productRangeLabel(listMeta)}
            </div>
          )}
          <div className="d-flex align-items-center flex-wrap">
            <label className="mr-2 mb-0 text-muted small" htmlFor="products-per-page">Rows per page</label>
            <select
              id="products-per-page"
              className="custom-select custom-select-sm mr-md-3 mb-2 mb-md-0"
              style={{ width: "auto" }}
              value={perPage}
              onChange={event => {
                setPerPage(event.target.value)
                setPage(1)
              }}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">Show all</option>
            </select>
            {!listMeta.show_all && listMeta.total_pages > 1 && (
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={listMeta.current_page <= 1}
                  onClick={() => setPage(Math.max(1, listMeta.current_page - 1))} />
                <Pagination.Item disabled>
                  {listMeta.current_page}
                  {" "}
                  /
                  {listMeta.total_pages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={listMeta.current_page >= listMeta.total_pages}
                  onClick={() => setPage(listMeta.current_page + 1)} />
              </Pagination>
            )}
          </div>
        </div>
      )}
      {requesting ? <Loader /> : <ProductsTable products={products} triggerArchive={triggerArchive} />}
    </Container>
  )
}

function ProductsTable({ products, triggerArchive }) {
  return (
    <div className="table-responsive">
      <table className="table table-listing table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Code</th>
            <th>Workflow</th>
            <th>Drafting due date count</th>
            <th>Selectable</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <ProductRow key={product.id} product={product} triggerArchive={triggerArchive} />
          ))}
        </tbody>
      </table>
      {products.length === 0 && <div className="text-center">No products match these filters.</div>}
    </div>
  )
}

function ProductRow({ product, triggerArchive }) {
  const { id, attributes } = product
  const [archiveRequested, setArchiveRequested] = useState(false)
  const archiveButtonText = attributes.archived ? "Restore" : "Archive"

  return (
    <tr>
      <td>
        {attributes.name}
        <ProductReviewIcon id={id} reasons={attributes.settingsReviewReasons} />
      </td>
      <td>{attributes.code || "-"}</td>
      <td>{workflowLabel(attributes.workflow)}</td>
      <td>{attributes.draftingDueDateCount || "-"}</td>
      <td>{attributes.selectable ? "Yes" : "No"}</td>
      <td>{attributes.archived ? "Archived" : "Active"}</td>
      <td className="text-right">
        <Link to={`/settings/products/${id}`}>Detail</Link>
        <span className="text-muted mx-2">|</span>
        <AnchorButton
          className="w-4rem mr-2"
          disabled={archiveRequested}
          onClick={async () => {
            setArchiveRequested(true)
            await triggerArchive(id, !attributes.archived)
            setArchiveRequested(false)
          }}>
          {archiveRequested ? (
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          ) : archiveButtonText}
        </AnchorButton>
      </td>
    </tr>
  )
}
